/**
 * Wagon Service
 * Wagon (vagon) işlemleri için service
 */

import { apiClient } from '../client/apiClient';
import { ENDPOINTS, createEndpoint } from '../config/endpoints';
import {
  Wagon,
  PaginatedResponse,
  BaseQueryParams,
  ID,
  ExportFormat
} from '../types/api.types';

/**
 * Wagon query parameters
 */
export interface WagonQueryParams extends BaseQueryParams {
  unitId?: ID | ID[];
  type?: string | string[];
  status?: string | string[];
  positionFrom?: number;
  positionTo?: number;
  includeBogies?: boolean;
  includeEquipment?: boolean;
  includeAlarms?: boolean;
}

/**
 * Create wagon request
 */
export interface CreateWagonRequest {
  wagonId: string;
  name: string;
  description?: string;
  type: string;
  unitId: ID;
  position: number;
  specifications?: Record<string, any>;
  metadata?: Record<string, any>;
}

/**
 * Update wagon request
 */
export interface UpdateWagonRequest {
  wagonId?: string;
  name?: string;
  description?: string;
  type?: string;
  position?: number;
  status?: string;
  specifications?: Record<string, any>;
  metadata?: Record<string, any>;
}

/**
 * Wagon bogie details
 */
export interface WagonBogie {
  id: ID;
  bogieId: string;
  name: string;
  type: string;
  position: number;
  status: string;
  wheels: Array<{
    id: ID;
    position: string;
    diameter: number;
    wear: number;
    lastReplacement?: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

/**
 * Wagon equipment
 */
export interface WagonEquipment {
  id: ID;
  equipmentId: string;
  name: string;
  type: string;
  status: string;
  specifications?: Record<string, any>;
  lastInspection?: string;
}

/**
 * Wagon Service Interface
 */
export interface IWagonService {
  // CRUD Operations
  getWagons(params?: WagonQueryParams): Promise<PaginatedResponse<Wagon>>;
  getWagon(id: ID): Promise<Wagon>;
  createWagon(wagon: CreateWagonRequest): Promise<Wagon>;
  updateWagon(id: ID, wagon: UpdateWagonRequest): Promise<Wagon>;
  deleteWagon(id: ID): Promise<void>;
  
  // Special Queries
  getWagonsByUnit(unitId: ID, params?: BaseQueryParams): Promise<PaginatedResponse<Wagon>>;
  getWagonByWagonId(wagonId: string): Promise<Wagon>;
  searchWagons(query: string, params?: BaseQueryParams): Promise<PaginatedResponse<Wagon>>;
  
  // Bogie Operations
  getWagonBogies(wagonId: ID): Promise<WagonBogie[]>;
  getBogieDetails(wagonId: ID, bogieId: ID): Promise<WagonBogie>;
  updateBogie(wagonId: ID, bogieId: ID, updates: Partial<WagonBogie>): Promise<WagonBogie>;
  
  // Equipment Operations
  getWagonEquipment(wagonId: ID): Promise<WagonEquipment[]>;
  getEquipmentDetails(wagonId: ID, equipmentId: ID): Promise<WagonEquipment>;
  updateEquipment(wagonId: ID, equipmentId: ID, updates: Partial<WagonEquipment>): Promise<WagonEquipment>;
  
  // Wheel Operations
  getWagonWheels(wagonId: ID): Promise<any[]>;
  getWheelDetails(wagonId: ID, wheelId: ID): Promise<any>;
  updateWheelWear(wagonId: ID, wheelId: ID, wear: number): Promise<any>;
  
  // Maintenance
  getMaintenanceHistory(wagonId: ID): Promise<any[]>;
  scheduleMaintenance(wagonId: ID, maintenance: any): Promise<any>;
  
  // Alarms
  getWagonAlarms(wagonId: ID, params?: BaseQueryParams): Promise<PaginatedResponse<any>>;
  getActiveAlarms(wagonId: ID): Promise<any[]>;
  
  // Export
  exportWagons(format: ExportFormat, params?: WagonQueryParams): Promise<Blob>;
  exportWagonReport(wagonId: ID, format: ExportFormat): Promise<Blob>;
  
  // Statistics
  getWagonStatistics(wagonId?: ID): Promise<any>;
  getWheelWearStatistics(wagonId: ID): Promise<any>;
}

/**
 * Wagon Service Implementation
 */
export class WagonService implements IWagonService {
  // ==================== CRUD OPERATIONS ====================

  async getWagons(params?: WagonQueryParams): Promise<PaginatedResponse<Wagon>> {
    try {
      const endpoint = ENDPOINTS.WAGONS.LIST;
      const cacheKey = this.generateCacheKey('wagons', params);
      
      return await apiClient.getList<Wagon>(endpoint, params, {
        cacheKey,
        cacheTTL: 2 * 60 * 1000, // 2 dakika
        params: this.normalizeWagonParams(params)
      });
    } catch (error) {
      console.error('Get wagons error:', error);
      throw error;
    }
  }

  async getWagon(id: ID): Promise<Wagon> {
    try {
      const endpoint = createEndpoint(ENDPOINTS.WAGONS.DETAIL, id);
      const cacheKey = `wagon_${id}`;
      
      const response = await apiClient.get<Wagon>(endpoint, id, {
        cacheKey,
        cacheTTL: 5 * 60 * 1000 // 5 dakika
      });
      
      return response.data;
    } catch (error) {
      console.error(`Get wagon ${id} error:`, error);
      throw error;
    }
  }

  async createWagon(wagon: CreateWagonRequest): Promise<Wagon> {
    try {
      const endpoint = ENDPOINTS.WAGONS.BASE;
      const response = await apiClient.customPost<Wagon>(endpoint, wagon);
      
      // Cache'i temizle
      this.clearWagonsCache();
      if (wagon.unitId) {
        this.clearUnitWagonsCache(wagon.unitId);
      }
      
      return response.data;
    } catch (error) {
      console.error('Create wagon error:', error);
      throw error;
    }
  }

  async updateWagon(id: ID, wagon: UpdateWagonRequest): Promise<Wagon> {
    try {
      const endpoint = createEndpoint(ENDPOINTS.WAGONS.DETAIL, id);
      const response = await apiClient.customPut<Wagon>(endpoint, wagon);
      
      // Cache'i temizle
      this.clearWagonCache(id);
      this.clearWagonsCache();
      if (response.data.unitId) {
        this.clearUnitWagonsCache(response.data.unitId);
      }
      
      return response.data;
    } catch (error) {
      console.error(`Update wagon ${id} error:`, error);
      throw error;
    }
  }

  async deleteWagon(id: ID): Promise<void> {
    try {
      const endpoint = createEndpoint(ENDPOINTS.WAGONS.DETAIL, id);
      await apiClient.delete(endpoint, id);
      
      // Cache'i temizle
      this.clearWagonCache(id);
      this.clearWagonsCache();
      // Unit cache'i de temizlemek için wagon bilgisini önce almalıyız
      // Veya farklı bir yaklaşım gerekebilir
    } catch (error) {
      console.error(`Delete wagon ${id} error:`, error);
      throw error;
    }
  }

  // ==================== SPECIAL QUERIES ====================

  async getWagonsByUnit(unitId: ID, params?: BaseQueryParams): Promise<PaginatedResponse<Wagon>> {
    try {
      const endpoint = `${ENDPOINTS.UNITS.BASE}/${unitId}/wagons`;
      const cacheKey = `unit_wagons_${unitId}_${JSON.stringify(params || {})}`;
      
      return await apiClient.getList<Wagon>(endpoint, params, {
        cacheKey,
        cacheTTL: 5 * 60 * 1000 // 5 dakika
      });
    } catch (error) {
      console.error(`Get wagons by unit ${unitId} error:`, error);
      throw error;
    }
  }

  async getWagonByWagonId(wagonId: string): Promise<Wagon> {
    try {
      const endpoint = `${ENDPOINTS.WAGONS.BASE}/by-wagon-id/${wagonId}`;
      const cacheKey = `wagon_by_id_${wagonId}`;
      
      const response = await apiClient.customGet<Wagon>(endpoint, {
        cacheKey,
        cacheTTL: 5 * 60 * 1000
      });
      
      return response.data;
    } catch (error) {
      console.error(`Get wagon by wagonId ${wagonId} error:`, error);
      throw error;
    }
  }

  async searchWagons(query: string, params?: BaseQueryParams): Promise<PaginatedResponse<Wagon>> {
    try {
      const endpoint = `${ENDPOINTS.WAGONS.BASE}/search`;
      const searchParams = {
        ...params,
        q: query
      };
      
      const cacheKey = `wagons_search_${query}_${JSON.stringify(params || {})}`;
      
      return await apiClient.getList<Wagon>(endpoint, searchParams, {
        cacheKey,
        cacheTTL: 2 * 60 * 1000
      });
    } catch (error) {
      console.error('Search wagons error:', error);
      throw error;
    }
  }

  // ==================== BOGIE OPERATIONS ====================

  async getWagonBogies(wagonId: ID): Promise<WagonBogie[]> {
    try {
      const endpoint = `${ENDPOINTS.WAGONS.BASE}/${wagonId}/bogies`;
      const cacheKey = `wagon_bogies_${wagonId}`;
      
      const response = await apiClient.customGet<WagonBogie[]>(endpoint, {
        cacheKey,
        cacheTTL: 5 * 60 * 1000
      });
      
      return response.data || [];
    } catch (error) {
      console.error(`Get wagon ${wagonId} bogies error:`, error);
      throw error;
    }
  }

  async getBogieDetails(wagonId: ID, bogieId: ID): Promise<WagonBogie> {
    try {
      const endpoint = `${ENDPOINTS.WAGONS.BASE}/${wagonId}/bogies/${bogieId}`;
      const cacheKey = `bogie_details_${wagonId}_${bogieId}`;
      
      const response = await apiClient.customGet<WagonBogie>(endpoint, {
        cacheKey,
        cacheTTL: 5 * 60 * 1000
      });
      
      return response.data;
    } catch (error) {
      console.error(`Get bogie ${bogieId} details error:`, error);
      throw error;
    }
  }

  async updateBogie(wagonId: ID, bogieId: ID, updates: Partial<WagonBogie>): Promise<WagonBogie> {
    try {
      const endpoint = `${ENDPOINTS.WAGONS.BASE}/${wagonId}/bogies/${bogieId}`;
      
      const response = await apiClient.customPut<WagonBogie>(endpoint, updates);
      
      // Cache'i temizle
      this.clearWagonBogiesCache(wagonId);
      this.clearBogieCache(wagonId, bogieId);
      
      return response.data;
    } catch (error) {
      console.error(`Update bogie ${bogieId} error:`, error);
      throw error;
    }
  }

  // ==================== EQUIPMENT OPERATIONS ====================

  async getWagonEquipment(wagonId: ID): Promise<WagonEquipment[]> {
    try {
      const endpoint = `${ENDPOINTS.WAGONS.BASE}/${wagonId}/equipment`;
      const cacheKey = `wagon_equipment_${wagonId}`;
      
      const response = await apiClient.customGet<WagonEquipment[]>(endpoint, {
        cacheKey,
        cacheTTL: 5 * 60 * 1000
      });
      
      return response.data || [];
    } catch (error) {
      console.error(`Get wagon ${wagonId} equipment error:`, error);
      throw error;
    }
  }

  async getEquipmentDetails(wagonId: ID, equipmentId: ID): Promise<WagonEquipment> {
    try {
      const endpoint = `${ENDPOINTS.WAGONS.BASE}/${wagonId}/equipment/${equipmentId}`;
      const cacheKey = `equipment_details_${wagonId}_${equipmentId}`;
      
      const response = await apiClient.customGet<WagonEquipment>(endpoint, {
        cacheKey,
        cacheTTL: 5 * 60 * 1000
      });
      
      return response.data;
    } catch (error) {
      console.error(`Get equipment ${equipmentId} details error:`, error);
      throw error;
    }
  }

  async updateEquipment(wagonId: ID, equipmentId: ID, updates: Partial<WagonEquipment>): Promise<WagonEquipment> {
    try {
      const endpoint = `${ENDPOINTS.WAGONS.BASE}/${wagonId}/equipment/${equipmentId}`;
      
      const response = await apiClient.customPut<WagonEquipment>(endpoint, updates);
      
      // Cache'i temizle
      this.clearWagonEquipmentCache(wagonId);
      this.clearEquipmentCache(wagonId, equipmentId);
      
      return response.data;
    } catch (error) {
      console.error(`Update equipment ${equipmentId} error:`, error);
      throw error;
    }
  }

  // ==================== WHEEL OPERATIONS ====================

  async getWagonWheels(wagonId: ID): Promise<any[]> {
    try {
      const endpoint = `${ENDPOINTS.WAGONS.BASE}/${wagonId}/wheels`;
      const cacheKey = `wagon_wheels_${wagonId}`;
      
      const response = await apiClient.customGet<any[]>(endpoint, {
        cacheKey,
        cacheTTL: 5 * 60 * 1000
      });
      
      return response.data || [];
    } catch (error) {
      console.error(`Get wagon ${wagonId} wheels error:`, error);
      throw error;
    }
  }

  async getWheelDetails(wagonId: ID, wheelId: ID): Promise<any> {
    try {
      const endpoint = `${ENDPOINTS.WAGONS.BASE}/${wagonId}/wheels/${wheelId}`;
      const cacheKey = `wheel_details_${wagonId}_${wheelId}`;
      
      const response = await apiClient.customGet<any>(endpoint, {
        cacheKey,
        cacheTTL: 5 * 60 * 1000
      });
      
      return response.data;
    } catch (error) {
      console.error(`Get wheel ${wheelId} details error:`, error);
      throw error;
    }
  }

  async updateWheelWear(wagonId: ID, wheelId: ID, wear: number): Promise<any> {
    try {
      const endpoint = `${ENDPOINTS.WAGONS.BASE}/${wagonId}/wheels/${wheelId}/wear`;
      
      const response = await apiClient.customPut<any>(endpoint, { wear });
      
      // Cache'i temizle
      this.clearWagonWheelsCache(wagonId);
      this.clearWheelCache(wagonId, wheelId);
      
      return response.data;
    } catch (error) {
      console.error(`Update wheel ${wheelId} wear error:`, error);
      throw error;
    }
  }

  // ==================== MAINTENANCE ====================

  async getMaintenanceHistory(wagonId: ID): Promise<any[]> {
    try {
      const endpoint = `${ENDPOINTS.WAGONS.BASE}/${wagonId}/maintenance`;
      const cacheKey = `wagon_maintenance_${wagonId}`;
      
      const response = await apiClient.customGet<any[]>(endpoint, {
        cacheKey,
        cacheTTL: 5 * 60 * 1000
      });
      
      return response.data || [];
    } catch (error) {
      console.error(`Get wagon ${wagonId} maintenance history error:`, error);
      throw error;
    }
  }

  async scheduleMaintenance(wagonId: ID, maintenance: any): Promise<any> {
    try {
      const endpoint = `${ENDPOINTS.WAGONS.BASE}/${wagonId}/maintenance`;
      
      const response = await apiClient.customPost<any>(endpoint, maintenance);
      
      // Cache'i temizle
      this.clearWagonMaintenanceCache(wagonId);
      
      return response.data;
    } catch (error) {
      console.error(`Schedule wagon ${wagonId} maintenance error:`, error);
      throw error;
    }
  }

  // ==================== ALARMS ====================

  async getWagonAlarms(wagonId: ID, params?: BaseQueryParams): Promise<PaginatedResponse<any>> {
    try {
      const endpoint = `${ENDPOINTS.WAGONS.BASE}/${wagonId}/alarms`;
      const cacheKey = `wagon_alarms_${wagonId}_${JSON.stringify(params || {})}`;
      
      return await apiClient.getList<any>(endpoint, params, {
        cacheKey,
        cacheTTL: 30 * 1000 // 30 saniye
      });
    } catch (error) {
      console.error(`Get wagon ${wagonId} alarms error:`, error);
      throw error;
    }
  }

  async getActiveAlarms(wagonId: ID): Promise<any[]> {
    try {
      const endpoint = `${ENDPOINTS.WAGONS.BASE}/${wagonId}/alarms/active`;
      const cacheKey = `wagon_active_alarms_${wagonId}`;
      
      const response = await apiClient.customGet<any[]>(endpoint, {
        cacheKey,
        cacheTTL: 15 * 1000 // 15 saniye
      });
      
      return response.data || [];
    } catch (error) {
      console.error(`Get wagon ${wagonId} active alarms error:`, error);
      throw error;
    }
  }

  // ==================== EXPORT ====================

  async exportWagons(format: ExportFormat, params?: WagonQueryParams): Promise<Blob> {
    try {
      const endpoint = `${ENDPOINTS.WAGONS.BASE}/export`;
      return await apiClient.exportData(endpoint, format, params);
    } catch (error) {
      console.error('Export wagons error:', error);
      throw error;
    }
  }

  async exportWagonReport(wagonId: ID, format: ExportFormat): Promise<Blob> {
    try {
      const endpoint = `${ENDPOINTS.WAGONS.BASE}/${wagonId}/export-report`;
      return await apiClient.exportData(endpoint, format);
    } catch (error) {
      console.error(`Export wagon ${wagonId} report error:`, error);
      throw error;
    }
  }

  // ==================== STATISTICS ====================

  async getWagonStatistics(wagonId?: ID): Promise<any> {
    try {
      const endpoint = wagonId 
        ? `${ENDPOINTS.WAGONS.BASE}/${wagonId}/statistics`
        : `${ENDPOINTS.WAGONS.BASE}/statistics`;
      
      const cacheKey = wagonId ? `wagon_stats_${wagonId}` : 'wagons_stats';
      
      const response = await apiClient.customGet<any>(endpoint, {
        cacheKey,
        cacheTTL: 5 * 60 * 1000
      });
      
      return response.data;
    } catch (error) {
      console.error('Get wagon statistics error:', error);
      throw error;
    }
  }

  async getWheelWearStatistics(wagonId: ID): Promise<any> {
    try {
      const endpoint = `${ENDPOINTS.WAGONS.BASE}/${wagonId}/statistics/wheel-wear`;
      const cacheKey = `wheel_wear_stats_${wagonId}`;
      
      const response = await apiClient.customGet<any>(endpoint, {
        cacheKey,
        cacheTTL: 5 * 60 * 1000
      });
      
      return response.data;
    } catch (error) {
      console.error(`Get wagon ${wagonId} wheel wear statistics error:`, error);
      throw error;
    }
  }

  // ==================== PRIVATE METHODS ====================

  private normalizeWagonParams(params?: WagonQueryParams): any {
    if (!params) return {};
    
    const normalized: any = { ...params };
    
    // Array'leri string'e çevir
    if (Array.isArray(normalized.unitId)) {
      normalized.unitId = normalized.unitId.join(',');
    }
    
    if (Array.isArray(normalized.type)) {
      normalized.type = normalized.type.join(',');
    }
    
    if (Array.isArray(normalized.status)) {
      normalized.status = normalized.status.join(',');
    }
    
    // Boolean'ları string'e çevir
    if (typeof normalized.includeBogies === 'boolean') {
      normalized.includeBogies = normalized.includeBogies.toString();
    }
    
    if (typeof normalized.includeEquipment === 'boolean') {
      normalized.includeEquipment = normalized.includeEquipment.toString();
    }
    
    if (typeof normalized.includeAlarms === 'boolean') {
      normalized.includeAlarms = normalized.includeAlarms.toString();
    }
    // Undefined veya null olanları kaldır
    for (const key in normalized) {
      if (normalized[key] === undefined || normalized[key] === null) {
        delete normalized[key];
      }
    }
    
    return normalized;
  }

  private generateCacheKey(prefix: string, params?: any): string {
    if (!params) return prefix;
    
    const paramString = JSON.stringify(params);
    const hash = this.hashString(paramString);
    return `${prefix}_${hash}`;
  }

  private hashString(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.codePointAt(i);
      if (char === undefined) continue;
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(36);
  }

  private clearWagonCache(wagonId: ID): void {
    apiClient.cancelRequest(`wagon_${wagonId}`);
  }

  private clearWagonsCache(): void {
    apiClient.cancelRequest('wagons');
  }

  private clearUnitWagonsCache(unitId: ID): void {
    apiClient.cancelRequest(`unit_wagons_${unitId}`);
  }

  private clearWagonBogiesCache(wagonId: ID): void {
    apiClient.cancelRequest(`wagon_bogies_${wagonId}`);
  }

  private clearBogieCache(wagonId: ID, bogieId: ID): void {
    apiClient.cancelRequest(`bogie_details_${wagonId}_${bogieId}`);
  }

  private clearWagonEquipmentCache(wagonId: ID): void {
    apiClient.cancelRequest(`wagon_equipment_${wagonId}`);
  }

  private clearEquipmentCache(wagonId: ID, equipmentId: ID): void {
    apiClient.cancelRequest(`equipment_details_${wagonId}_${equipmentId}`);
  }

  private clearWagonWheelsCache(wagonId: ID): void {
    apiClient.cancelRequest(`wagon_wheels_${wagonId}`);
  }

  private clearWheelCache(wagonId: ID, wheelId: ID): void {
    apiClient.cancelRequest(`wheel_details_${wagonId}_${wheelId}`);
  }

  private clearWagonMaintenanceCache(wagonId: ID): void {
    apiClient.cancelRequest(`wagon_maintenance_${wagonId}`);
  }
}

// Singleton instance
export const wagonService = new WagonService();

// Default export
export default wagonService;